import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";

export interface SurveyQuestionAnswerListState {
    state: LoadStateInfo;
    request?: RetrieveSurveyQuestionAnswerListRequest;
    list?: SurveyQuestionAnswerInfo[];
}

const initialState: SurveyQuestionAnswerListState = {
    state: notStarted(),
};

export interface RetrieveSurveyQuestionAnswerListRequest {
    surveyId: string;
    userId?: string;
}

export const retrieveSurveyQuestionAnswerList = createAsyncThunk(
    "retrieveSurveyQuestionAnswerList",
    async (param: RetrieveSurveyQuestionAnswerListRequest, thunkAPI) => {
        console.debug(`retrieve survey question answers for ${param.surveyId}`);

        let url = `${getBackendUrl()}/api/survey/question/answer/list/${param.surveyId}`;
        if (param.userId) {
            url += `?userId=${param.userId}`;
        }

        const response = await fetch(url, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyQuestionAnswerListRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.surveyQuestionAnswerList;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveSurveyQuestionAnswerList skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const surveyQuestionAnswerListSlice = createSlice({
    name: "surveyQuestionAnswerList",
    initialState,
    reducers: {
        check: (state, action: PayloadAction<RetrieveSurveyQuestionAnswerListRequest>) => {
            if (state.state.state != LoadState.NotStarted
                && (state.request && state.request.surveyId === action.payload.surveyId && state.request.userId === action.payload.userId)
            ) {
                console.trace(`survey question answer list already retrieved for survey ${action.payload.surveyId}`);
            } else {
                console.debug(`survey question answer list not retrieved for survey ${action.payload.surveyId}`);
                state.state = notStarted();
                state.request = action.payload;
                state.list = undefined;
            }
        },
        clean: (state) => {
            state.state = notStarted();
            state.list = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSurveyQuestionAnswerList.pending, (state, action) => {
                state.state = loading();
                state.list = undefined;
                state.request = action.meta.arg;
            })
            .addCase(retrieveSurveyQuestionAnswerList.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.list = action.payload;
            })
            .addCase(retrieveSurveyQuestionAnswerList.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export interface SurveyQuestionAnswerInfo {
    id: string;
    surveyId: string;
    surveyQuestionId: string;
    surveyQuestionOptionId?: string;
    studentId: string;
    userId: string;
    answeredAt: string;
}

export const { check, clean } = surveyQuestionAnswerListSlice.actions;

export const selectSurveyQuestionAnswerList = (state: RootState) => state.surveyQuestionAnswerList;

export default surveyQuestionAnswerListSlice.reducer;
